import {
  type GetReferralCodesQuery,
  useGetReferralCodesQuery,
} from '@/__generated__/graphql';
import { Button, Input, Space, Table, message } from 'antd';
import { useCallback, useState } from 'react';
import { useTreatyClient } from '../../lib/client';

type ReferralCode = GetReferralCodesQuery['referral_codes'][number];

export default function ReferralCodesPage() {
  const [newCode, setNewCode] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const {
    loading: isLoading,
    data,
    refetch,
  } = useGetReferralCodesQuery({
    context: {
      headers: {
        'x-hasura-role': 'admin',
      },
    },
  });
  const client = useTreatyClient();

  const filteredData = data?.referral_codes.filter((code) =>
    code.code.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleCreateCode = useCallback(async () => {
    if (!newCode || isCreating) return;

    setIsCreating(true);
    try {
      const response =
        await client.api.users.invitation_codes.referral.create.post({
          code: newCode,
        });
      if (response.data?.success) {
        message.success({
          content: 'Referral code created successfully',
          key: 'create-referral-code-success',
        });
        setNewCode('');
        refetch();
      }
    } catch (error) {
      console.error('Failed to create referral code:', error);
      message.error({
        content: 'Failed to create referral code',
        key: 'create-referral-code-error',
      });
    } finally {
      setIsCreating(false);
    }
  }, [newCode, isCreating, client, refetch]);

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Usage Count',
      key: 'usage_count',
      render: (record: ReferralCode) =>
        record.user_referrals_aggregate?.aggregate?.count ?? 0,
      sorter: (a: ReferralCode, b: ReferralCode) =>
        (a.user_referrals_aggregate?.aggregate?.count ?? 0) -
        (b.user_referrals_aggregate?.aggregate?.count ?? 0),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a: ReferralCode, b: ReferralCode) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
  ];

  return (
    <div className="space-y-8 p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-semibold">Referral Codes</h1>
        <Space>
          <Input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder="Enter referral code"
            className="w-[200px]"
          />
          <Button
            type="primary"
            onClick={handleCreateCode}
            loading={isCreating}
            disabled={!newCode || isCreating}
          >
            Create Code
          </Button>
        </Space>
      </div>
      <Input.Search
        placeholder="Search referral codes"
        allowClear
        onChange={(e) => setSearchText(e.target.value)}
        className="mb-4 w-[300px]"
      />
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={isLoading}
      />
    </div>
  );
}
